import {
  addDays,
  differenceInCalendarDays,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  parse,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import type { Event } from '~/shared/types'

export type CalendarEventState = 'upcoming' | 'past' | 'draft' | 'canceled'

export interface CalendarEntry {
  key: string
  event: Event
  start: Date
  end: Date
  isAllDay: boolean
  state: CalendarEventState
}

const WEEK_STARTS_ON = 1

/** The days of the month view: full Monday-to-Sunday weeks covering the whole month. */
export function buildMonthGrid(month: Date): Date[] {
  const first = startOfWeek(startOfMonth(month), { weekStartsOn: WEEK_STARTS_ON })
  const last = endOfWeek(endOfMonth(month), { weekStartsOn: WEEK_STARTS_ON })
  const count = differenceInCalendarDays(last, first) + 1
  return Array.from({ length: count }, (_, i) => addDays(first, i))
}

/** `[from, to)` spanning every day of the month view. */
export function monthGridRange(month: Date): { from: Date; to: Date } {
  const days = buildMonthGrid(month)
  return { from: days[0], to: addDays(days[days.length - 1], 1) }
}

export function dayKey(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

export function monthParam(date: Date): string {
  return format(date, 'yyyy-MM')
}

/** The first day of the month a `yyyy-MM` search param names, or null when it names none. */
export function parseMonthParam(value: unknown): Date | null {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}$/.test(value)) return null
  const date = parse(value, 'yyyy-MM', new Date())
  return isValid(date) ? date : null
}

function calendarEventState(event: Event, end: Date, now: Date): CalendarEventState {
  if (event.isCanceled) return 'canceled'
  if (event.isDraft) return 'draft'
  return end < now ? 'past' : 'upcoming'
}

/** Each event on the day it starts, once per occurrence, sorted by start time within a day. */
export function groupEntriesByDay(events: Event[], now: Date): Map<string, CalendarEntry[]> {
  const byDay = new Map<string, CalendarEntry[]>()
  for (const event of events) {
    const slots =
      event.occurrences.length > 0
        ? event.occurrences.map((o) => ({
            key: `${event.id}:${o.id}`,
            start: o.startTime,
            end: o.endTime,
            isAllDay: o.isAllDay,
          }))
        : [{ key: event.id, start: event.startTime, end: event.endTime, isAllDay: false }]
    for (const slot of slots) {
      const start = new Date(slot.start)
      const end = new Date(slot.end)
      const entry: CalendarEntry = {
        key: slot.key,
        event,
        start,
        end,
        isAllDay: slot.isAllDay,
        state: calendarEventState(event, end, now),
      }
      const k = dayKey(start)
      const list = byDay.get(k)
      if (list) list.push(entry)
      else byDay.set(k, [entry])
    }
  }
  for (const list of byDay.values()) list.sort((a, b) => a.start.getTime() - b.start.getTime())
  return byDay
}
